import { useEffect, useState } from "react";
import styled, { css } from "styled-components";
import { getLocale } from "react-i18nify";
import { useForm, useFieldArray } from "react-hook-form";
import * as yup from "yup";
import useYupValidationResolver from "src/hooks/useYupValidationResolver";
// import dayjs from "dayjs";
import { useSnackbar } from "notistack";

import { styled as muiStyled } from "@mui/material/styles";
import {
  Autocomplete,
  Box,
  createFilterOptions,
  Grid,
  Stack,
} from "@mui/material";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Divider from "@mui/material/Divider";

import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InsertPhotoIcon from "@mui/icons-material/InsertPhoto";
import AddIcon from "@mui/icons-material/Add";

import { updatePurchaseOrderDetail } from "src/api/purchaseOrder";
import { searchProductAdmin } from "src/api/product";
// import { getPromoCodeList } from "src/api/promoCode";

import font from "src/styles/font";
import device from "src/styles/device";
import {
  checkNotEmpty,
  convertIntToCurrency,
  convertPrice,
  formatFromElastic,
} from "src/common";
import { Black, DarkGrey, Primary, White01 } from "src/styles/color";

import { UseUpload } from "src/context/UploadProductContext";
import { useDialog } from "src/context/DialogContext";
import Dialog from "src/components/core/Dialog";
import Button from "src/components/core/Button";
import TextField from "src/components/core/TextField";
import { useAuth } from "src/hooks/useAuth";

const CloseButton = styled(IconButton)`
  &.MuiIconButton-root {
    position: absolute;
    top: 16px;
    right: 16px;
    color: ${Black};
  }
`;

const WrapperTable = styled.div`
  width: 100%;
  /* min-width: 500px; */
  overflow: hidden;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  .MuiGrid-item {
    padding: 8px 12px;
  }

  @media ${device.tabletL} {
    .MuiGrid-item {
      padding: 8px;
    }
  }

  ::-webkit-scrollbar {
    width: 5px;
    height: 7px;
    display: none;
  }
  /* Track */
  ::-webkit-scrollbar-track {
    background: transparent;
  }
  /* Handle */
  ::-webkit-scrollbar-thumb {
    background: var(--grey-color);
    border-radius: 100px;
  }
  /* Handle on hover */
  ::-webkit-scrollbar-thumb:hover {
    background: var(--grey-color);
  }
  ::-webkit-scrollbar-corner {
    background: transparent;
  }
`;

const DivTableHeader = styled(Grid)`
  width: 100%;
  min-width: 1000px;
  /* padding: 8px 12px; */
  background: #f3f1f2;
  align-items: center;

  @media ${device.tabletL} {
    min-width: 780px;
  }
  @media ${device.mobileL} {
    min-width: 720px;
  }
`;

const GridHeader = styled(Grid)`
  width: 100%;
  align-items: center;
`;

const HeaderLabel = styled.div`
  color: ${Black};
  font-size: ${font.size.sm};
  font-weight: 500;
  line-height: 24px;
  text-align: center;
  margin: auto;

  @media ${device.tabletL} {
    font-size: ${font.size.xs};
  }
`;

const TextLabel = styled.div`
  color: ${Black};
  font-size: ${font.size.sm};
  font-weight: 400;
  line-height: 24px;
  text-align: left;

  @media ${device.tabletL} {
    font-size: ${font.size.xs};
  }
`;

const DivTableDetail = styled(Grid)`
  width: 100%;
  min-width: 1000px;
  border-bottom: 1px solid #d9d9d9;
  text-align: center;

  @media ${device.tabletL} {
    min-width: 780px;
  }
  @media ${device.mobileL} {
    min-width: 720px;
  }
`;

const SpanTitle = styled.div`
  font-size: ${font.size.md};
  font-weight: 600;
  line-height: 24px;

  @media ${device.mobileL} {
    font-size: ${font.size.sm};
  }
`;

const StyledTextField = styled(TextField)`
  .MuiInputBase-adornedEnd {
    padding-right: 2px;
  }

  .MuiAutocomplete-root .MuiOutlinedInput-root {
    padding-left: 12px;
  }
`;

const ImageEmpty = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  width: 80px;
  height: 80px;
  background: #f2f1f3;
  border-radius: 16px;
  object-fit: cover;
  object-position: center;
  cursor: pointer;
  vertical-align: initial;
  margin-right: 8px;
  &:hover {
    opacity: 0.8;
  }

  @media ${device.tabletL} {
    width: 64px;
    height: 64px;
    border-radius: 8px;
  }
  @media ${device.mobileL} {
    width: 48px;
    height: 48px;
  }

  &.active {
    border: 2px solid #707070;
  }
`;

const Image = styled.img`
  width: 80px;
  height: 80px;
  background: #f2f1f3;
  border-radius: 16px;
  object-fit: cover;
  object-position: center;
  margin-right: 8px;
  &:hover {
    opacity: 0.8;
  }

  @media ${device.tabletL} {
    width: 64px;
    height: 64px;
    border-radius: 8px;
  }
  @media ${device.mobileL} {
    width: 48px;
    height: 48px;
  }

  &.active {
    border: 2px solid #707070;
  }
`;

const TextOption = styled(Box)`
  width: 100%;
  color: ${({ color }) => (color === "grey" ? DarkGrey : Black)};
  font-size: ${font.size.sm};
  font-weight: 400;
  margin-bottom: 8px;

  @media ${device.tabletL} {
    font-size: ${font.size.xs};

    ${({ nowrap }) =>
      nowrap
        ? css`
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          `
        : css`
            white-space: pre-line;
            word-break: break-word;
          `}
  }
`;

const WrapperCalOrder = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  margin: 0;
  margin-top: 16px;
  gap: 8px;

  @media ${device.mobile} {
    margin-top: 8px;
  }
`;

const RowSumPrice = styled.div`
  margin-left: 0;
  margin-right: 0;
  display: flex;
  /* align-items: center; */
  justify-content: space-between;
`;

const FocusRowSumPrice = styled(RowSumPrice)`
  margin-top: 16px;
  align-items: flex-end;

  @media ${device.tablet} {
    margin-top: 8px;
  }
`;

const Price = styled.p`
  margin: 0;
  color: ${(props) => (props.fontColor ? props.fontColor : Black)};
  font-size: ${font.size.md};
  font-weight: ${(props) => (props.bold ? 600 : 400)};
  text-align: ${(props) => (props.align ? props.align : "left")};

  ${({ desc, focus }) =>
    desc
      ? css`
          display: flex;
          flex-direction: column;

          span {
            font-size: ${font.size.xs};
            font-weight: 400;
            color: ${DarkGrey};
            /* text-decoration: underline;
            margin-left: 10px;
            cursor: pointer; */
          }
        `
      : focus
        ? css`
            span {
              font-size: ${font.size.xl};
              font-weight: 600;
              color: ${Primary};
            }
          `
        : css``}

  @media ${device.tablet} {
    font-size: ${font.size.sm};

    ${({ desc, focus }) =>
      desc
        ? css`
            span {
              font-size: ${font.size.xs};
            }
          `
        : focus
          ? css`
              span {
                font-size: ${font.size.lg};
              }
            `
          : css``}
  }
`;

const WrapperProductCondition = styled.div`
  margin-top: 4px;
  width: 100%;

  /* read-only */
  .MuiInputBase-root.MuiOutlinedInput-root.Mui-readOnly {
    background-color: ${White01};
  }
  .MuiInputBase-root.MuiOutlinedInput-root.Mui-readOnly
    .MuiOutlinedInput-input {
    background: ${White01};
    border-radius: 16px;
  }
`;

const SpanPromoCode = styled.div`
  font-size: ${font.size.md};
  font-weight: 400;
  line-height: 24px;

  @media ${device.mobileL} {
    font-size: ${font.size.sm};
  }
`;

const StyledDialog = muiStyled(Dialog)(({ theme }) => ({
  "& .MuiPaper-root": {
    borderRadius: 16,
  },
  "& .MuiDialogContent-root": {
    maxHeight: "500px",
    border: 0,
    padding: 24,
    paddingTop: 0,
  },
  "& .MuiDialog-container": {
    "& .MuiPaper-root": {
      width: "100%",
      maxWidth: 1200,
    },
  },
  "& .MuiDialogActions-root": {
    justifyContent: "center",
  },
  "& .MuiDialogActions-root>:not(:first-of-type)": {
    marginLeft: 24,
  },

  [theme.breakpoints.down("sm")]: {
    "& .MuiPaper-root": {
      margin: 24,
    },
    "& .MuiDialogContent-root": {
      padding: 16,
      paddingTop: 0,
    },
    "& .MuiDialogActions-root": {
      padding: 16,
    },
  },
}));

const validationSchema = yup.object().shape({
  productChoiceOrders: yup
    .array()
    .of(
      yup.object({
        productChoiceOrder: yup.object().required(),
        costPrice: yup.number(),
        price: yup.number().required(),
        volume: yup.number().required().min(1),
        discount: yup.number(),
        total: yup.number(),
        installDetailGroup: yup.object().shape({
          isService: yup.boolean(),
          installArea: yup.number().nullable(),
          servicePrice: yup.number().nullable(),
          serviceNote: yup.string().nullable(),
          totalInstall: yup.number().nullable(),
        }),
      }),
    )
    .test({
      name: "required",
      message: "Product list should not empty product field.",
      test: (array, context) => {
        if (array.length > 1) {
          return array.every(({ productChoiceOrder }) => !!productChoiceOrder);
        }
        return true;
      },
    })
    .min(1)
    .required(),
  condition: yup.string(),
  promoCodeRef: yup.object().nullable(),
});

const EditOrderDialog = ({ orderDetail, onClose, onSuccess, setIsLoading }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { masterdata } = UseUpload();
  const { openDialog } = useDialog();
  const { checkAllowPermission } = useAuth();
  // const adminList = [
  //   "ast.md@xsurface.com",
  //   "bd@xsurface.com",
  //   "service@xsurface.com",
  //   "accountadmin@xsurface.com",
  //   "webdev@xsurface.com",
  // ];

  const color_data = masterdata.color_data;

  // ! mock data
  // const [isShowPromocode, setIsShowPromocode] = useState(false);
  const [isAddInstaller, setIsAddInstaller] = useState(false);
  // !!

  // const [isLoading, setIsLoading] = useState(false);

  const [dataProductList, setDataProductList] = useState([]);
  // const [promoCodes, setPromoCodes] = useState([]);

  // ! name not sure
  const defaultValues = {
    productChoiceOrders: [],
    condition: "",
    promoCodeRef: null,
  };
  const [updateOrder, setUpdateOrder] = useState(defaultValues);
  let resolver = useYupValidationResolver(validationSchema);

  const {
    formState: { errors: formErrors, isValid },
    control: formControl,
    watch,
    trigger,
    getValues,
    setValue: setFormValue,
    reset,
  } = useForm({
    resolver,
    defaultValues,
  });

  // console.log("formState -> formErrors", formErrors);
  // console.log("formState -> isValid", isValid);

  const { append: appendProduct, remove: removeProduct } = useFieldArray({
    control: formControl,
    name: "productChoiceOrders",
  });

  const checkIsAddinstaller = () => {
    let test = updateOrder?.productChoiceOrders?.some((product, index) =>
      ["installArea", "serviceNote", "servicePrice", "totalInstall"].some(
        (item) => {
          let objVal = product.installDetailGroup[item];
          return item === "serviceNote" ? checkNotEmpty(objVal) : objVal !== 0;
        },
      ),
    );

    setIsAddInstaller(test);
  };

  useEffect(() => {
    if (!orderDetail) return;

    fetchProduct();
    // fetchPromoCodes();
    reset({
      ...orderDetail,
      productChoiceOrders: orderDetail?.productChoiceOrders?.map((product) => ({
        ...product,
        costPrice: product?.costPrice || product?.price,
        // ! hold promocode
        // discount: product?.discount || 0,
        discount: !!orderDetail?.discount?.promoCodeRef?._id
          ? 0
          : product?.discount || 0,
        total: !!orderDetail?.discount?.promoCodeRef?._id
          ? +product?.price * +product?.volume +
            (+product?.installDetailGroup?.totalInstall +
              +product?.installDetailGroup?.servicePrice)
          : product?.total,
      })),
      // ! hold promocode
      // promoCodeRef: orderDetail?.discount?.promoCodeRef,
      promoCodeRef: null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderDetail]);

  // const fetchPromoCodes = async () => {
  //   const results = await getPromoCodeList();
  //   setPromoCodes(results);
  // };

  const fetchProduct = async () => {
    if (!orderDetail?.seller?._id) return;

    await searchProductAdmin({
      seller_id: orderDetail?.seller?._id,
    }).then(async ({ data }) => {
      // console.log("------ elastic ------", data);
      setDataProductList(
        data?.rows.length > 0
          ? data?.rows?.map((product) => ({
              ...formatFromElastic(product),
            }))
          : [],
      );
    });
  };

  /**
   * @notice after form value change, then set value to formData state
   */
  useEffect(() => {
    const subscription = watch((values, { name, type }) => {
      setUpdateOrder(values);
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  /**
   * @notice trigger validation when formData updated
   */
  useEffect(() => {
    trigger();
    // console.log("==== updateOrder ====", updateOrder);

    checkIsAddinstaller();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateOrder]);

  useEffect(() => {
    let orderValue = getValues();

    reset({
      ...orderValue,
      totalInstaller: sumTotalInstaller(),
      totalDiscount: sumTotalDiscount(),
      totalPrice: sumTotalPrice(),
      vat: calculateVat(),
      totalVat: sumTotalPrice() + calculateVat(),
      totalAll: sumTotalPrice() + calculateVat(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateOrder.productChoiceOrders.length]);

  const sumTotalProductPrice = () => {
    let choiceData = getValues();
    let priceArray = [];
    priceArray = choiceData?.productChoiceOrders?.map(
      (product) => +product.price * +product.volume,
    );
    return [...priceArray]?.reduce((a, b) => a + b, 0) || 0 || 0;
  };

  const sumTotalInstaller = () => {
    let choiceData = getValues();
    let priceArray = [];
    priceArray = choiceData?.productChoiceOrders?.map(
      (product) =>
        +product?.installDetailGroup?.totalInstall +
        +product?.installDetailGroup?.servicePrice,
    );
    return priceArray?.reduce((a, b) => a + b, 0) || 0;
  };

  const sumTotalDiscount = () => {
    let choiceData = getValues();
    let priceArray = [];
    priceArray = choiceData?.productChoiceOrders?.map(
      (product) => +product.discount,
    );
    return priceArray?.reduce((a, b) => a + b, 0) || 0;
  };

  const sumTotalPrice = () => {
    return (
      sumTotalProductPrice() + sumTotalInstaller() - sumTotalDiscount() || 0
    );
  };

  const calculateVat = () => {
    return (sumTotalPrice() * 7) / 100 || 0;
  };

  const handleChange = async (event, replaceName, replaceValue) => {
    // console.log('---- e ---', event, '===', replaceName, '--- replaceValue ---', replaceValue)
    let name =
      typeof replaceName !== "undefined" ? replaceName : event.target.name;
    let value =
      typeof replaceValue !== "undefined" ? replaceValue : event.target.value;

    let path = name.split(".");
    let [key, ...rest] = path;

    let editingKey = key;
    let updated;

    if (key === "productChoiceOrders") {
      let [objIndex, objKey, subObjKey] = rest;
      editingKey = `${key}.${objIndex}`;
      let editingRow = getValues(editingKey);

      // console.log("---- editingRow ---", editingRow, "--- value ---", value, objKey);

      if (!checkNotEmpty(objKey) && value === null) {
        updated = {
          productChoiceOrder: null,
          volume: 0,
          costPrice: 0,
          price: 0,
          discount: 0,
          total: 0,
          installDetailGroup: {
            isService: false,
            installArea: 0,
            servicePrice: 0,
            serviceNote: "",
            totalInstall: 0,
          },
        };
      } else if (objKey === "productChoiceOrder") {
        let orderValue = getValues();
        updated = {
          ...editingRow,
          productChoiceOrder: value,
          volume: 1,
          costPrice: !!value.group_price?.costPrice
            ? value.group_price?.costPrice
            : value.group_price?.price, // ??
          price: value.group_price?.is_start_price
            ? value.group_price?.start_price
            : value.group_price?.special_price > 0 &&
                value.group_price?.special_price !== null &&
                value.group_price?.special_price !== undefined
              ? value.group_price?.special_price
              : value.group_price?.price,
          discount: 0,
          total: orderValue?.promoCodeRef?._id
            ? value.group_price?.price // ! not finish
            : value.group_price?.is_start_price
              ? value.group_price?.start_price
              : value.group_price?.special_price > 0 &&
                  value.group_price?.special_price !== null &&
                  value.group_price?.special_price !== undefined
                ? value.group_price?.special_price
                : value.group_price?.price,
          installDetailGroup: {
            isService: false,
            installArea: 0,
            servicePrice: 0,
            serviceNote: "",
            totalInstall: 0,
          },
        };
      } else if (objKey === "volume") {
        updated = {
          ...editingRow,
          volume: value,
          total:
            +editingRow.price * +value -
            +editingRow?.discount +
            (+editingRow?.installDetailGroup?.totalInstall +
              +editingRow?.installDetailGroup?.servicePrice),
        };
      } else if (objKey === "price") {
        updated = {
          ...editingRow,
          price: value,
          total:
            +value * +editingRow.volume -
            +editingRow?.discount +
            (+editingRow?.installDetailGroup?.totalInstall +
              +editingRow?.installDetailGroup?.servicePrice),
        };
      } else if (objKey === "discount") {
        updated = {
          ...editingRow,
          discount: value,
          total:
            +editingRow.price * +editingRow.volume -
            +value +
            (+editingRow?.installDetailGroup?.totalInstall +
              +editingRow?.installDetailGroup?.servicePrice),
        };
      } else if (objKey === "installDetailGroup") {
        // remove install detail
        if (value === null) {
          updated = {
            ...editingRow,
            installDetailGroup: {
              isService: false,
              installArea: 0,
              servicePrice: 0,
              serviceNote: "",
              totalInstall: 0,
            },
          };
        } else if (subObjKey === "installArea") {
          updated = {
            ...editingRow,
            installDetailGroup: {
              ...editingRow.installDetailGroup,
              isService: true,
              installArea: value,
            },
          };
        } else if (subObjKey === "servicePrice") {
          updated = {
            ...editingRow,
            installDetailGroup: {
              ...editingRow.installDetailGroup,
              isService: true,
              servicePrice: value,
            },
            total:
              +editingRow.price * +editingRow.volume -
              +editingRow?.discount +
              (+value + +editingRow?.installDetailGroup?.totalInstall),
          };
        } else if (subObjKey === "totalInstall") {
          updated = {
            ...editingRow,
            installDetailGroup: {
              ...editingRow.installDetailGroup,
              isService: true,
              totalInstall: value,
            },
            total:
              +editingRow.price * +editingRow.volume -
              +editingRow?.discount +
              (+editingRow?.installDetailGroup?.servicePrice + +value),
          };
        } else {
          updated = {
            ...editingRow,
            installDetailGroup: {
              ...editingRow.installDetailGroup,
              [subObjKey]: value,
            },
          };
        }
      } else {
        updated = {
          ...editingRow,
          [objKey]: value,
        };
      }
    } else if (key === "promoCodeRef") {
      if (value === null) {
        let editingChoice = getValues("productChoiceOrders");

        let updatedChoice;
        // eslint-disable-next-line array-callback-return
        editingChoice.map((choice, index) => {
          updatedChoice = {
            ...choice,
            discount: 0,
            total: +choice?.price * +choice?.volume,
          };

          setFormValue(`productChoiceOrders[${index}]`, updatedChoice);
        });

        updated = null;
      } else {
        updated = {
          // ...value,
          promoCode: value.promo_code,
          promoName: value.promo_name,
          promoTitle: value.promo_title,
          promoCondition: {
            promoConditionUser: value.promo_condition?.promo_condition_user,
            promoConditionRepeat: value.promo_condition?.promo_condition_repeat,
            promoConditionRepeatValue:
              value.promo_condition?.promo_condition_repeat_value,
          },
          promoStartTime: value.promo_start_time,
          promoExpireTime: value.promo_expire_time,
          promoType: value.promo_pay_type,
          promoDescription: value.promo_description,
          usedAmount: value.used_amount,
        };
      }
    } else {
      updated = value;
    }

    if (editingKey !== "") {
      setFormValue(editingKey, updated);
    }

    let orderValue = await getValues();

    await reset({
      ...orderValue,
      totalInstaller: sumTotalInstaller(),
      totalDiscount: sumTotalDiscount(),
      totalPrice: sumTotalPrice(),
      vat: calculateVat(),
      totalVat: sumTotalPrice() + calculateVat(),
      totalAll:
        updateOrder?.statusPayment?.status === "deposit"
          ? +updateOrder?.statusPayment?.depositPrice
          : sumTotalPrice() + calculateVat(),
      outstandingBalance:
        updateOrder?.statusPayment?.status === "deposit"
          ? sumTotalPrice() +
            calculateVat() -
            +updateOrder?.statusPayment?.depositPrice
          : 0,
    });

    await checkIsAddinstaller();
  };

  const onSubmit = async () => {
    try {
      setIsLoading(true);

      const { promoCodeRef, ...updateRest } = updateOrder;
      const updateParams = {
        ...updateRest,
        paymentType:
          updateOrder?.statusPayment?.status === "deposit" ? "minimum" : "full",
        discount: {
          ...updateOrder.discount,
          promoCodeRef: promoCodeRef,
        },
      };

      await updatePurchaseOrderDetail(orderDetail?._id, {
        orders: [updateParams],
      });
      enqueueSnackbar(`Update Order Success!`, {
        variant: "success",
      });
      await onSuccess();
    } catch (err) {
      console.error("err:", err.response);
      enqueueSnackbar(err.response.data.message, {
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    openDialog({
      type: "confirm",
      title: `คุณต้องการออกจากการแก้ไขคำสั่งซื้อใช่หรือไม่?`,
      onConfirm: () => {
        onClose();
      },
      maxWidth: 400,
    });
  };

  const filterOptions = createFilterOptions({
    matchFrom: "any",
    limit: 100,
  });

  return (
    <StyledDialog
      onClose={handleClose}
      aria-labelledby="customized-dialog-title"
      open={true}
    >
      <DialogTitle
        sx={{
          p: {
            sm: 3,
            xs: 2,
          },
          display: "flex",
          alignItems: "center",
          gap: 1,
          fontSize: {
            sm: font.size.lg,
            xs: font.size.md,
          },
          fontWeight: 600,
        }}
      >
        แก้ไขคำสั่งซื้อ
        <CloseButton aria-label="close" onClick={handleClose}>
          <CloseIcon fontSize="small" />
        </CloseButton>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <WrapperTable>
              <DivTableHeader container>
                <GridHeader
                  item
                  // lg={adminList.includes(user?.email) ? 5 : 6.3}
                  // md={adminList.includes(user?.email) ? 5 : 6.2}
                  // xs={adminList.includes(user?.email) ? 4 : 5.4}
                  {...(checkAllowPermission("products.can_view_costPrice")
                    ? { lg: 5, md: 5, xs: 4 }
                    : { lg: 6.3, md: 6.2, xs: 5.4 })}
                >
                  <HeaderLabel style={{ textAlign: "left" }}>
                    สินค้า
                  </HeaderLabel>
                </GridHeader>
                {checkAllowPermission("products.can_view_costPrice") && (
                  <GridHeader item lg={1.3} md={1.2} xs={1.4}>
                    <HeaderLabel>ราคาต้นทุน</HeaderLabel>
                  </GridHeader>
                )}
                <GridHeader item lg={1.6} md={1.6} xs={1.9}>
                  <HeaderLabel>ราคาขายต่อหน่วย</HeaderLabel>
                </GridHeader>

                <GridHeader item lg={1} md={1.1} xs={1.3}>
                  <HeaderLabel>จำนวน</HeaderLabel>
                </GridHeader>

                <GridHeader item lg={1.3} md={1.3} xs={1.4}>
                  <HeaderLabel>ส่วนลด</HeaderLabel>
                </GridHeader>

                <GridHeader item lg={1.3} md={1.3} xs={1.5}>
                  <HeaderLabel>ราคารวม</HeaderLabel>
                </GridHeader>

                <GridHeader item xs={0.5}></GridHeader>
              </DivTableHeader>

              {updateOrder?.productChoiceOrders?.map((product, index) => (
                <DivTableDetail container key={`order-product-${index}`}>
                  <Grid
                    item
                    // lg={adminList.includes(user?.email) ? 5 : 6.3}
                    // md={adminList.includes(user?.email) ? 5 : 6.2}
                    // xs={adminList.includes(user?.email) ? 4 : 5.4}
                    {...(checkAllowPermission("products.can_view_costPrice")
                      ? { lg: 5, md: 5, xs: 4 }
                      : { lg: 6.3, md: 6.2, xs: 5.4 })}
                  >
                    <Autocomplete
                      aria-required={true}
                      id="search-product-select"
                      isOptionEqualToValue={(option, value) =>
                        option._id === value._id
                      }
                      renderOption={(props, option) => (
                        <li
                          {...props}
                          style={{
                            borderBottom: "4px solid var(--darkwhite-color)",
                            ":lastChild": {
                              borderBottom: "none",
                            },
                          }}
                          key={`product-quotation-${option._id}`}
                        >
                          <Box
                            sx={{
                              width: "100%",
                              py: 2,
                              display: "flex",
                            }}
                          >
                            <Box sx={{ mr: 1 }}>
                              {!!option?.group_image?.product_display_image ===
                              false ? (
                                <ImageEmpty>
                                  <InsertPhotoIcon
                                    sx={{
                                      color: "rgba(0, 0, 0, 0.54)",
                                      cursor: "pointer",
                                    }}
                                  />
                                </ImageEmpty>
                              ) : (
                                <Image
                                  src={
                                    option?.group_image?.product_display_image
                                      ? option?.group_image?.product_display_image.replace(
                                          ".com",
                                          ".com/300x300",
                                        )
                                      : ""
                                  }
                                  alt={
                                    option?.group_image?.product_display_image
                                  }
                                />
                              )}
                            </Box>

                            <Box
                              sx={{
                                width: "100%",
                                maxWidth: "calc(100% - 88px)",
                              }}
                            >
                              <TextOption>
                                {option?.product?.item_name}
                              </TextOption>

                              <TextOption color="grey">
                                {option?.product?.item_code}
                              </TextOption>

                              <Box
                                sx={{
                                  width: "100%",
                                  display: "flex",
                                  alignItems: "center",
                                  mb: 1,
                                }}
                              >
                                <img
                                  style={{
                                    width: "16px",
                                    height: "16px",
                                    objectFit: "cover",
                                    objectPosition: "center",
                                    borderRadius: "50%",
                                  }}
                                  src={
                                    color_data.find(
                                      (detail) =>
                                        detail._id === option?.color._id,
                                    )?.icon_img || ""
                                  }
                                  alt={
                                    option?.group_image?.product_display_image
                                  }
                                />

                                <TextOption color="grey" sx={{ ml: 1, mb: 0 }}>
                                  {getLocale() === "th"
                                    ? option?.color?.color_th
                                    : option?.color?.color_en}
                                </TextOption>
                              </Box>

                              <TextOption color="grey">
                                {option?.size?.quantity === "size"
                                  ? `${option?.size?.width} ${
                                      getLocale() === "th"
                                        ? option?.size?.unit_width?.unit_th
                                        : option?.size?.unit_width?.unit_en
                                    } x ${option?.size?.height} ${
                                      getLocale() === "th"
                                        ? option?.size?.unit_height?.unit_th
                                        : option?.size?.unit_height?.unit_en
                                    } x ${option?.size?.thickness} ${
                                      getLocale() === "th"
                                        ? option?.size?.unit_thickness?.unit_th
                                        : option?.size?.unit_thickness?.unit_en
                                    }`
                                  : option?.size?.quantity === "capacity"
                                    ? `${option?.size?.capacity_size} ${
                                        getLocale() === "th"
                                          ? option?.size?.unit_capacity?.unit_th
                                          : option?.size?.unit_capacity?.unit_en
                                      }`
                                    : getLocale() === "th"
                                      ? "สินค้าสั่งทำพิเศษ"
                                      : "Made by order"}
                              </TextOption>

                              <TextOption color="grey">
                                {`${convertPrice(
                                  option?.group_price?.is_start_price === true
                                    ? option?.group_price?.start_price
                                    : option?.group_price.special_price > 0 &&
                                        option?.group_price.special_price !==
                                          null &&
                                        option?.group_price.special_price !==
                                          undefined
                                      ? option?.group_price.special_price
                                      : option?.group_price?.price,
                                )}/${
                                  getLocale() === "th"
                                    ? option?.group_price?.unit_price?.unit_th
                                    : option?.group_price?.unit_price?.unit_en
                                }`}
                              </TextOption>
                            </Box>
                          </Box>
                        </li>
                      )}
                      options={dataProductList}
                      value={product.productChoiceOrder}
                      onChange={(event, newValue) => {
                        handleChange(
                          null,
                          `productChoiceOrders.${index}.productChoiceOrder`,
                          newValue,
                        );
                      }}
                      getOptionLabel={(option) => {
                        return (
                          option?.product?.item_name +
                          " - " +
                          option?.product?.item_code +
                          (option?.name !== ""
                            ? ` - (` + option?.name + `)`
                            : ``)
                        );
                      }}
                      filterOptions={filterOptions}
                      fullWidth
                      sx={{
                        "&.MuiAutocomplete-inputRoot .MuiInputBase-root": {
                          pr: 5,
                        },
                        "& .MuiOutlinedInput-root .MuiAutocomplete-input": {
                          p: {
                            sm: "3px 4px",
                            xs: 0,
                          },
                        },
                      }}
                      renderInput={(params) => (
                        <StyledTextField
                          {...params}
                          placeholder="เพิ่มสินค้า"
                          // error={
                          //   !!formErrors[
                          //     `productChoiceOrders[${index}].productChoiceOrder._id`
                          //   ]
                          // }
                        />
                      )}
                    />
                  </Grid>
                  {checkAllowPermission("products.can_view_costPrice") && (
                    <Grid item lg={1.3} md={1.2} xs={1.4}>
                      <StyledTextField
                        variant="outlined"
                        fullWidth
                        // InputProps={{
                        //   readOnly: true,
                        // }}
                        value={product?.costPrice}
                        onChange={(e) => {
                          if (e.target.value * 0 === 0)
                            handleChange(
                              null,
                              `productChoiceOrders.${index}.costPrice`,
                              e.target.value,
                            );
                        }}
                        onBlur={(e) => {
                          if (e.target.value * 0 === 0)
                            handleChange(
                              null,
                              `productChoiceOrders.${index}.costPrice`,
                              parseFloat(e.target.value) || 0,
                            );
                        }}
                        error={
                          !!formErrors[`productChoiceOrders.${index}.costPrice`]
                        }
                      />
                    </Grid>
                  )}

                  <Grid item lg={1.6} md={1.6} xs={1.9}>
                    <StyledTextField
                      variant="outlined"
                      fullWidth
                      value={product?.price}
                      onChange={(e) => {
                        if (e.target.value * 0 === 0)
                          handleChange(
                            null,
                            `productChoiceOrders.${index}.price`,
                            e.target.value,
                          );
                      }}
                      onBlur={(e) => {
                        if (e.target.value * 0 === 0)
                          handleChange(
                            null,
                            `productChoiceOrders.${index}.price`,
                            parseFloat(e.target.value) || 0,
                          );
                      }}
                      error={!!formErrors[`productChoiceOrders.${index}.price`]}
                    />
                  </Grid>

                  <Grid item lg={1} md={1.1} xs={1.3}>
                    <StyledTextField
                      variant="outlined"
                      fullWidth
                      value={product?.volume}
                      onChange={(e) => {
                        if (e.target.value * 0 === 0)
                          handleChange(
                            null,
                            `productChoiceOrders.${index}.volume`,
                            e.target.value,
                          );
                      }}
                      onBlur={(e) => {
                        if (e.target.value * 0 === 0)
                          handleChange(
                            null,
                            `productChoiceOrders.${index}.volume`,
                            parseFloat(e.target.value) || 0,
                          );
                      }}
                      error={
                        !!formErrors[`productChoiceOrders.${index}.volume`]
                      }
                      // helperText={
                      //   !!formErrors[`productChoiceOrders.${index}.volume`]
                      //     ? formErrors[`productChoiceOrders.${index}.volume`]
                      //         .message
                      //     : ""
                      // }
                    />
                  </Grid>

                  <Grid item lg={1.3} md={1.3} xs={1.4}>
                    <StyledTextField
                      variant="outlined"
                      fullWidth
                      value={product?.discount || 0}
                      onChange={(e) => {
                        if (e.target.value * 0 === 0)
                          handleChange(
                            null,
                            `productChoiceOrders.${index}.discount`,
                            e.target.value,
                          );
                      }}
                      onBlur={(e) => {
                        if (e.target.value * 0 === 0)
                          handleChange(
                            null,
                            `productChoiceOrders.${index}.discount`,
                            parseFloat(e.target.value) || 0,
                          );
                      }}
                      error={
                        !!formErrors[`productChoiceOrders.${index}.discount`]
                      }
                      // helperText={
                      //   !!formErrors[`promo_start_time`]
                      //     ? formErrors[`promo_start_time`].message
                      //     : ""
                      // }
                    />
                  </Grid>

                  <Grid item lg={1.3} md={1.3} xs={1.5}>
                    <StyledTextField
                      variant="outlined"
                      fullWidth
                      InputProps={{ readOnly: true }}
                      value={+product?.price * +product?.volume}
                      // error={!!formErrors[`promo_start_time`]}
                      // helperText={
                      //   !!formErrors[`promo_start_time`]
                      //     ? formErrors[`promo_start_time`].message
                      //     : ""
                      // }
                    />
                  </Grid>

                  <Grid
                    item
                    xs={0.5}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      "&.MuiGrid-item": { px: 0 },
                    }}
                  >
                    <IconButton
                      sx={{ p: 0.5, color: Primary }}
                      disabled={updateOrder?.productChoiceOrders.length < 2}
                      onClick={() => {
                        removeProduct(index);
                      }}
                    >
                      <DeleteOutlineIcon
                        sx={{
                          fontSize: font.size.lg,
                        }}
                      />
                    </IconButton>
                  </Grid>
                </DivTableDetail>
              ))}
            </WrapperTable>
          </Grid>

          <Grid item xs={12}>
            <Button
              variant="outlined"
              onClick={() => {
                appendProduct({
                  productChoiceOrder: null,

                  costPrice: 0,
                  price: 0,
                  volume: 0,
                  discount: 0,
                  total: 0,

                  installDetailGroup: {
                    isService: false,
                    installArea: 0,
                    servicePrice: 0,
                    serviceNote: "",
                    totalInstall: 0,
                  },
                });
              }}
              // sx={{ maxWidth: 180 }}
              startIcon={<AddIcon />}
            >
              เพิ่มรายการสินค้า
            </Button>
          </Grid>

          <Grid item xs={12}>
            <SpanTitle>เงื่อนไขสินค้า</SpanTitle>

            <WrapperProductCondition>
              <TextField
                className="TextField-without-border-radius"
                placeholder="เงื่อนไขสินค้า"
                fullWidth
                multiline
                rows={3}
                value={updateOrder?.condition}
                onChange={(e) => handleChange(e, `condition`)}
                InputLabelProps={{ shrink: true }}
                // value={orderDetail?.ref_pay_no ?? ""}
              />
            </WrapperProductCondition>
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {/* can select promocode (hold) */}
          {/* <Grid item xs={12}>
            <SpanTitle>คูปองส่วนลด</SpanTitle>
          </Grid>

          <Grid item xs={12}>
            {!!updateOrder?.promoCodeRef ? (
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <SpanPromoCode>
                  {updateOrder?.promoCodeRef?.promoCode}
                </SpanPromoCode>

                <Stack direction="row" alignItems="center" gap={2}>
                  <SpanPromoCode>
                    -
                    {convertIntToCurrency(
                      updateOrder?.productChoiceOrders
                        ?.map((product) => product?.discount)
                        .reduce((a, b) => a + b, 0) || 0,
                    )}{" "}
                    บาท
                  </SpanPromoCode>

                  <IconButton
                    sx={{ p: 0.5 }}
                    onClick={() => {
                      openDialog({
                        type: "confirm",
                        title: `คุณต้องการต้องการลบคูปองส่วนลดใช่หรือไม่?`,
                        onConfirm: () => {
                          handleChange(null, `promoCodeRef`, null);
                          handleClose();
                        },
                        maxWidth: 342,
                      });
                    }}
                  >
                    <DeleteOutlineIcon
                      sx={{ color: Primary, fontSize: font.size.lg }}
                    />
                  </IconButton>
                </Stack>
              </Stack>
            ) : (
              <Stack direction="row" alignItems="center" gap={2}>
                // all promo code can used
                <Button
                  variant="outlined"
                  onClick={() => setIsShowPromocode(true)}
                  startIcon={<AddIcon />}
                >
                  เพิ่ม {`XSF100TH`}
                </Button>
              </Stack>
            )}
          </Grid> */}

          {!!updateOrder?.promoCodeRef?._id && (
            <>
              <Grid item xs={12}>
                <SpanTitle>คูปองส่วนลด</SpanTitle>
              </Grid>

              <Grid item xs={12}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <SpanPromoCode>
                    {updateOrder?.promoCodeRef?.promoCode}
                  </SpanPromoCode>

                  <Stack direction="row" alignItems="center" gap={2}>
                    <SpanPromoCode>
                      -
                      {convertIntToCurrency(
                        updateOrder?.productChoiceOrders
                          ?.map((product) => product?.discount)
                          .reduce((a, b) => a + b, 0) || 0,
                      )}{" "}
                      บาท
                    </SpanPromoCode>

                    <IconButton
                      sx={{ p: 0.5 }}
                      onClick={() => {
                        openDialog({
                          type: "confirm",
                          title: `คุณต้องการต้องการลบ\nคูปองส่วนลดใช่หรือไม่?`,
                          onConfirm: () => {
                            handleChange(null, `promoCodeRef`, null);
                            handleClose();
                          },
                          maxWidth: 342,
                        });
                      }}
                    >
                      <DeleteOutlineIcon
                        sx={{ color: Primary, fontSize: font.size.lg }}
                      />
                    </IconButton>
                  </Stack>
                </Stack>
              </Grid>

              <Grid item xs={12}>
                <Divider />
              </Grid>
            </>
          )}

          <Grid item xs={12}>
            <SpanTitle>ราคาช่าง</SpanTitle>
          </Grid>

          <Grid item xs={12}>
            {isAddInstaller ? (
              <WrapperTable>
                <DivTableHeader container>
                  <GridHeader item xs={1.5}>
                    <HeaderLabel style={{ textAlign: "left" }}>
                      รหัสสินค้า
                    </HeaderLabel>
                  </GridHeader>

                  <GridHeader item md={4.5} sm={3.8} xs={3.5}>
                    <HeaderLabel style={{ textAlign: "left" }}>
                      รายละเอียดการบริการ
                    </HeaderLabel>
                  </GridHeader>

                  <GridHeader item md={1.6} sm={1.9} xs={2}>
                    <HeaderLabel>ราคาช่าง/บาท</HeaderLabel>
                  </GridHeader>

                  <GridHeader item md={1.3} sm={1.4} xs={1.5}>
                    <HeaderLabel>พื้นที่(ตร.ม)</HeaderLabel>
                  </GridHeader>

                  <GridHeader item md={1.3} sm={1.4} xs={1.5}>
                    <HeaderLabel>ค่าบริการเพิ่มเติม</HeaderLabel>
                  </GridHeader>

                  <GridHeader item md={1.3} sm={1.5} xs={1.5}>
                    <HeaderLabel>ราคารวม</HeaderLabel>
                  </GridHeader>

                  <GridHeader item xs={0.5}></GridHeader>
                </DivTableHeader>

                {updateOrder?.productChoiceOrders?.map((product, index) => (
                  <DivTableDetail container key={`installer-detail-${index}`}>
                    <Grid item xs={1.5}>
                      <TextLabel>
                        {product?.productChoiceOrder?.product?.item_code}
                      </TextLabel>
                    </Grid>

                    <Grid item md={4.5} sm={3.8} xs={3.5}>
                      <StyledTextField
                        variant="outlined"
                        fullWidth
                        value={product?.installDetailGroup?.serviceNote}
                        onChange={(e) => {
                          handleChange(
                            e,
                            `productChoiceOrders.${index}.installDetailGroup.serviceNote`,
                          );
                        }}
                        error={
                          !!formErrors[
                            `productChoiceOrders.${index}.installDetailGroup.serviceNote`
                          ]
                        }
                        // helperText={
                        //   !!formErrors[`promo_start_time`]
                        //     ? formErrors[`promo_start_time`].message
                        //     : ""
                        // }
                      />
                    </Grid>

                    <Grid item md={1.6} sm={1.9} xs={2}>
                      <StyledTextField
                        variant="outlined"
                        fullWidth
                        value={product?.installDetailGroup?.totalInstall}
                        onChange={(e) => {
                          if (e.target.value * 0 === 0)
                            handleChange(
                              null,
                              `productChoiceOrders.${index}.installDetailGroup.totalInstall`,
                              e.target.value,
                            );
                        }}
                        onBlur={(e) => {
                          if (e.target.value * 0 === 0)
                            handleChange(
                              null,
                              `productChoiceOrders.${index}.installDetailGroup.totalInstall`,
                              parseFloat(e.target.value) || 0,
                            );
                        }}
                        error={
                          !!formErrors[
                            `productChoiceOrders.${index}.installDetailGroup.totalInstall`
                          ]
                        }
                      />
                    </Grid>

                    <Grid item md={1.3} sm={1.4} xs={1.5}>
                      <StyledTextField
                        variant="outlined"
                        fullWidth
                        value={product?.installDetailGroup?.installArea}
                        onChange={(e) => {
                          if (e.target.value * 0 === 0)
                            handleChange(
                              null,
                              `productChoiceOrders.${index}.installDetailGroup.installArea`,
                              e.target.value,
                            );
                        }}
                        onBlur={(e) => {
                          if (e.target.value * 0 === 0)
                            handleChange(
                              null,
                              `productChoiceOrders.${index}.installDetailGroup.installArea`,
                              parseFloat(e.target.value) || 0,
                            );
                        }}
                        error={
                          !!formErrors[
                            `productChoiceOrders.${index}.installDetailGroup.installArea`
                          ]
                        }
                      />
                    </Grid>

                    <Grid item md={1.3} sm={1.4} xs={1.5}>
                      <StyledTextField
                        variant="outlined"
                        fullWidth
                        value={product?.installDetailGroup?.servicePrice}
                        onChange={(e) => {
                          if (e.target.value * 0 === 0)
                            handleChange(
                              null,
                              `productChoiceOrders.${index}.installDetailGroup.servicePrice`,
                              e.target.value,
                            );
                        }}
                        onBlur={(e) => {
                          if (e.target.value * 0 === 0)
                            handleChange(
                              null,
                              `productChoiceOrders.${index}.installDetailGroup.servicePrice`,
                              parseFloat(e.target.value) || 0,
                            );
                        }}
                        error={
                          !!formErrors[
                            `productChoiceOrders.${index}.installDetailGroup.servicePrice`
                          ]
                        }
                      />
                    </Grid>

                    <Grid item md={1.3} sm={1.5} xs={1.5}>
                      <StyledTextField
                        variant="outlined"
                        fullWidth
                        InputProps={{ readOnly: true }}
                        value={
                          +product?.installDetailGroup?.totalInstall +
                          +product?.installDetailGroup?.servicePrice
                        }
                      />
                    </Grid>

                    <Grid
                      item
                      xs={0.5}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        "&.MuiGrid-item": { px: 0 },
                      }}
                    >
                      <IconButton
                        sx={{ p: 0.5 }}
                        onClick={() => {
                          handleChange(
                            null,
                            `productChoiceOrders.${index}.installDetailGroup`,
                            null,
                          );
                        }}
                      >
                        <DeleteOutlineIcon
                          sx={{ color: Primary, fontSize: font.size.lg }}
                        />
                      </IconButton>
                    </Grid>
                  </DivTableDetail>
                ))}
              </WrapperTable>
            ) : (
              <Button
                variant="outlined"
                onClick={() => setIsAddInstaller(true)}
                startIcon={<AddIcon />}
              >
                เพิ่มราคาช่าง
              </Button>
            )}
          </Grid>

          <Grid item xs={12}>
            <SpanTitle color={Primary}>
              {getLocale() === "th"
                ? "สรุปใบสั่งซื้อ"
                : "Purchase order summary"}
            </SpanTitle>

            <WrapperCalOrder>
              <RowSumPrice>
                <Price desc>
                  {getLocale() === "th" ? "ราคาสินค้า" : "Product price"}
                </Price>
                <Price>
                  {convertIntToCurrency(
                    updateOrder?.totalPriceOfProduct ||
                      updateOrder?.productChoiceOrders
                        ?.map((product) => product.price * product.volume)
                        .reduce((a, b) => a + b, 0),
                  )}{" "}
                  บาท
                </Price>
              </RowSumPrice>

              <RowSumPrice>
                <Price desc>
                  {getLocale() === "th" ? "ราคาช่าง" : "Service price"}
                </Price>
                <Price>
                  {!!updateOrder?.totalInstaller
                    ? convertIntToCurrency(updateOrder?.totalInstaller)
                    : convertIntToCurrency(
                        updateOrder?.totalPriceOfService ||
                          updateOrder?.productChoiceOrders
                            ?.filter(
                              (product) =>
                                product?.installDetailGroup?.isService === true,
                            )
                            ?.map(
                              (product) =>
                                (product?.productChoiceOrder?.group_price
                                  ?.install_price || 0) *
                                  product?.installDetailGroup?.installArea +
                                product?.installDetailGroup?.servicePrice,
                            )
                            .reduce((a, b) => a + b, 0) ||
                          0,
                      )}{" "}
                  บาท
                </Price>
              </RowSumPrice>

              <RowSumPrice>
                <Price desc>
                  {!!updateOrder?.promoCodeRef?._id
                    ? getLocale() === "th"
                      ? "คูปองส่วนลด"
                      : "Discount coupon"
                    : getLocale() === "th"
                      ? "ส่วนลด"
                      : "Discount"}
                </Price>
                <Price>
                  {convertIntToCurrency(
                    updateOrder?.productChoiceOrders
                      ?.map((product) => product?.discount)
                      .reduce((a, b) => a + b, 0) || 0,
                  )}{" "}
                  บาท
                </Price>
              </RowSumPrice>

              <RowSumPrice>
                <Price desc>{getLocale() === "th" ? "รวม" : "Subtotal"}</Price>
                <Price>
                  {convertIntToCurrency(updateOrder?.totalPrice)} บาท
                </Price>
              </RowSumPrice>

              <RowSumPrice>
                <Price desc>
                  {getLocale() === "th" ? "ภาษี 7%" : "Vat 7%"}
                </Price>
                <Price>{convertIntToCurrency(updateOrder?.vat)} บาท</Price>
              </RowSumPrice>

              {updateOrder?.statusPayment?.paymentType === "credit-card" && (
                <RowSumPrice>
                  <Price desc>
                    {getLocale() === "th" ? "ค่าธรรมเนียม" : "Credit card fee"}
                  </Price>
                  <Price>
                    {convertIntToCurrency(
                      updateOrder?.statusPayment?.depositPrice
                        ? updateOrder?.totalAll * 0.03
                        : 0,
                    )}{" "}
                    บาท
                  </Price>
                </RowSumPrice>
              )}

              <RowSumPrice>
                <Price desc>
                  {getLocale() === "th" ? "ราคารวมทั้งหมด" : "Grand total"}
                </Price>
                <Price>
                  {updateOrder?.statusPayment?.paymentType === "credit-card"
                    ? convertIntToCurrency(updateOrder?.totalAll * 1.03)
                    : convertIntToCurrency(updateOrder?.totalVat)}{" "}
                  บาท
                </Price>
              </RowSumPrice>

              {updateOrder?.statusPayment?.status === "deposit" && (
                <>
                  <RowSumPrice>
                    <Price desc>
                      {getLocale() === "th" ? "ยอดมัดจำ" : "Deposit price"}
                    </Price>
                    <Price>
                      {updateOrder?.statusPayment?.paymentType === "credit-card"
                        ? convertIntToCurrency(
                            updateOrder?.statusPayment?.depositPrice * 1.03,
                          )
                        : convertIntToCurrency(
                            updateOrder?.statusPayment?.status === "deposit"
                              ? updateOrder?.statusPayment?.depositPrice
                              : 0,
                          )}{" "}
                      บาท
                    </Price>
                  </RowSumPrice>

                  <FocusRowSumPrice>
                    <Price desc bold>
                      {getLocale() === "th"
                        ? "ยอดค้างชำระ"
                        : "Outstanding Balance"}
                    </Price>
                    <Price focus>
                      <span>
                        {/* {updateOrder?.statusPayment?.paymentType ===
                        "credit-card"
                          ? convertIntToCurrency(updateOrder?.totalAll * 1.03)
                          : convertIntToCurrency(
                              updateOrder?.outstandingBalance,
                            )} */}
                        {convertIntToCurrency(updateOrder?.outstandingBalance)}
                      </span>{" "}
                      บาท
                    </Price>
                  </FocusRowSumPrice>
                </>
              )}
            </WrapperCalOrder>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          variant="outlined"
          color="secondary"
          size="large"
          onClick={() => handleClose()}
          fullWidth
          sx={{ maxWidth: 180 }}
        >
          ยกเลิก
        </Button>

        <Button
          variant="contained"
          size="large"
          fullWidth
          onClick={() => {
            openDialog({
              type: "confirm",
              title: `คุณต้องการบันทึกการแก้ไขคำสั่งซื้อใช่หรือไม่?`,
              onConfirm: () => {
                onSubmit();
              },
              maxWidth: 400,
            });
          }}
          disabled={!isValid}
          sx={{ maxWidth: 180 }}
        >
          ยืนยัน
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default EditOrderDialog;
